/**
 * Custom course editor (UGC) — paste any zh/en sentence pairs, get a playable
 * course on the spot. Supports flexible delimiters: '=', '|', tab, '——'.
 *
 * Why this matters: most language apps lock you into their content. With a
 * UGC editor a user can drop in lyrics, lines from a play, a friend's email
 * — and turn it into a puzzle/typing/speaking lesson immediately.
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'motion/react';
import { Emoji } from '@/components/Emoji';
import { GlassButton } from '@/components/GlassButton';
import { Reveal } from '@/components/Reveal';
import { SectionHeading } from '@/components/SectionHeading';
import { SpotlightCard } from '@/components/SpotlightCard';
import { useAppStore } from '@/lib/store';
import type { CustomCourse } from '@/lib/types';

const SAMPLE = `Take it easy. = 放松点。
Could you do me a favor? = 能帮我个忙吗？
I have no idea what you mean. = 我完全不明白你的意思。
That's a piece of cake. = 这事儿小菜一碟。`;

const DELIMITERS = /\s*(=|——|\|+|\t+)\s*/;

interface Parsed {
  ok: { en: string; zh: string }[];
  bad: { line: string; reason: string }[];
}

function parse(text: string): Parsed {
  const out: Parsed = { ok: [], bad: [] };
  for (const raw of text.split('\n')) {
    const line = raw.trim();
    if (line === '' || line.startsWith('#')) continue;
    const parts = line.split(DELIMITERS).filter((p) => p !== undefined && !/^(=|——|\|+|\t+)$/.test(p) && p.trim().length > 0);
    if (parts.length < 2) {
      out.bad.push({ line, reason: '找不到分隔符（=、|、Tab）' });
      continue;
    }
    // Auto-detect which side is English (has more A-Za-z letters).
    const [a, b] = [parts[0]!.trim(), parts.slice(1).join(' ').trim()];
    const ratio = (s: string): number => s.split('').filter((c) => /[A-Za-z]/.test(c)).length / Math.max(1, s.length);
    if (ratio(a) > ratio(b)) {
      out.ok.push({ en: a, zh: b });
    } else {
      out.ok.push({ en: b, zh: a });
    }
  }
  return out;
}

const EMOJIS = ['🎬', '📖', '🎵', '🎮', '🎯', '🚀', '⚡', '🦄', '🍿', '🎨', '🪐', '🔥'];

export function CreateCoursePage(): JSX.Element {
  const navigate = useNavigate();
  const [raw, setRaw] = useState(SAMPLE);
  const [title, setTitle] = useState('我的私人课');
  const [emoji, setEmoji] = useState('🎯');
  const parsed = useMemo(() => parse(raw), [raw]);
  const addCustomCourse = useAppStore((s) => s.addCustomCourse);

  function save(): void {
    if (parsed.ok.length === 0) return;
    const id = `custom-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const course: CustomCourse = {
      id,
      title: title.trim() || '我的私人课',
      description: `${parsed.ok.length} 个句子 · 由你自己编辑`,
      emoji,
      createdAt: Date.now(),
      sentences: parsed.ok.map((s, i) => ({ id: `${id}-s${i}`, en: s.en, zh: s.zh })),
    };
    addCustomCourse(course);
    // v4: redirect to the new course's detail page directly — much more
    // satisfying than dumping the user back into the courses grid.
    navigate(`/app/courses/${id}?just=1`);
  }

  return (
    <div className="space-y-10">
      <Reveal>
        <SectionHeading
          eyebrow="UGC Editor · 自己造课"
          title={<>把任何句子，<span className="text-gradient-warm">立刻变成一节课</span></>}
          subtitle="美剧台词、邮件原文、歌词、笔记 — 粘进来就能玩。三种模式立刻可用。"
          align="left"
        />
      </Reveal>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* —— Editor —— */}
        <Reveal>
          <SpotlightCard>
            <div className="space-y-4">
              <div>
                <label className="text-xs uppercase tracking-[0.18em] text-honey-300 font-arcade">
                  课程标题
                </label>
                <input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="mt-2 w-full rounded-2xl bg-ink-800/60 border border-honey-400/30 px-4 py-2.5 text-white focus:outline-none focus:border-honey-400/80"
                />
              </div>

              <div>
                <label className="text-xs uppercase tracking-[0.18em] text-honey-300 font-arcade">
                  封面 emoji
                </label>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {EMOJIS.map((e) => (
                    <button
                      key={e}
                      onClick={() => setEmoji(e)}
                      className={`h-10 w-10 rounded-xl border transition-all grid place-items-center ${
                        emoji === e
                          ? 'bg-honey-400/20 border-honey-400/80 scale-110 glow-cyan'
                          : 'bg-ink-800/60 border-white/[0.08] hover:border-honey-400/40'
                      }`}
                    >
                      <Emoji char={e} size={24} />
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between">
                  <label className="text-xs uppercase tracking-[0.18em] text-honey-300 font-arcade">
                    句对 · 每行一个
                  </label>
                  <button
                    onClick={() => setRaw(SAMPLE)}
                    className="text-xs text-cream-400 hover:text-honey-300"
                  >
                    重置示例
                  </button>
                </div>
                <textarea
                  value={raw}
                  onChange={(e) => setRaw(e.target.value)}
                  rows={12}
                  spellCheck={false}
                  placeholder={`英文 = 中文\n或者：中文 | 英文`}
                  className="mt-2 w-full rounded-2xl bg-ink-900/80 border border-honey-400/20 px-4 py-3 text-cream-100 font-mono text-sm focus:outline-none focus:border-honey-400/60 resize-y leading-relaxed"
                />
                <div className="mt-2 text-xs text-cream-400 font-mono">
                  支持分隔符：<span className="text-honey-300">=</span> · <span className="text-honey-300">|</span> · Tab · ——
                </div>
              </div>
            </div>
          </SpotlightCard>
        </Reveal>

        {/* —— Preview —— */}
        <Reveal delay={0.05}>
          <SpotlightCard spotlightColor="rgba(170, 255, 0, 0.22)">
            <div className="flex items-start gap-4 mb-4">
              <Emoji char={emoji} size={44} />
              <div>
                <div className="font-display text-xl font-bold text-white">{title || '（未命名）'}</div>
                <div className="text-xs text-cream-400 mt-1">
                  <span className="text-mint-300 font-bold">{parsed.ok.length}</span> 条解析成功
                  {parsed.bad.length > 0 && <> · <span className="text-ember-300 font-bold">{parsed.bad.length}</span> 条出错</>}
                </div>
              </div>
            </div>

            <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
              {parsed.ok.length === 0 && parsed.bad.length === 0 && (
                <div className="text-center py-12 text-cream-400 text-sm">
                  开始粘贴 ← 实时预览
                </div>
              )}
              {parsed.ok.map((s, i) => (
                <motion.div
                  key={`${s.en}-${i}`}
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="rounded-2xl bg-ink-800/60 border border-white/[0.06] px-3 py-2"
                >
                  <div className="text-xs text-cream-400 truncate">{s.zh}</div>
                  <div className="font-mono text-mint-200 text-sm truncate">{s.en}</div>
                </motion.div>
              ))}
              {parsed.bad.map((b, i) => (
                <div
                  key={`bad-${i}`}
                  className="rounded-2xl bg-ember-500/[0.08] border border-ember-500/30 px-3 py-2 text-xs"
                >
                  <div className="text-ember-300">⚠ {b.reason}</div>
                  <div className="font-mono text-cream-300 truncate">{b.line}</div>
                </div>
              ))}
            </div>

            <div className="mt-6 pt-4 border-t border-white/[0.06]">
              <GlassButton
                onClick={save}
                variant="solid"
                size="lg"
                className="w-full"
                disabled={parsed.ok.length === 0}
              >
                生成课程 → {parsed.ok.length} 句
              </GlassButton>
              <div className="mt-2 text-xs text-cream-400 text-center">
                生成后立即可用 拼图 / 打字 / 口语 三种模式
              </div>
            </div>
          </SpotlightCard>
        </Reveal>
      </div>

      {/* Pro tips */}
      <Reveal>
        <div className="rounded-3xl border border-white/[0.06] bg-white/[0.02] p-6">
          <div className="font-display text-lg font-bold text-white mb-3">💡 玩法</div>
          <ul className="space-y-2 text-sm text-cream-300/90">
            <li>📺 把你正在追的美剧某集字幕双语对照粘进来</li>
            <li>📧 把一封想吃透的英文邮件拆成几句</li>
            <li>🎵 一首歌的歌词，背了一年不如玩一遍</li>
            <li>📚 老师讲过的语法点例句，自己出题考自己</li>
          </ul>
        </div>
      </Reveal>
    </div>
  );
}
